import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES } from "@/lib/format";
import { getChartTheme } from "@/lib/chartTheme";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function formatTick(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

export default function Reports() {
  const [revenue, setRevenue] = useState(null);
  const [sites, setSites] = useState(null);
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(isoDaysAgo(0));
  const { theme } = useAdminTheme();
  const chart = getChartTheme(theme);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports/revenue", { params: { from, to } });
        setRevenue(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, [from, to]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports/site-performance");
        setSites(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, []);

  return (
    <div>
      <PageHeader testId="reports-header" title="Reports" subtitle="Revenue and per-site performance" />

      <div className="p-4 md:p-8 space-y-6">
        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// REVENUE</div>
              <div className="font-heading font-bold text-xl mt-1">Daily Revenue</div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 font-mono text-xs">
                <input
                  type="date" data-testid="reports-revenue-from" value={from} max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-9 px-2 bg-nhp-bg border border-nhp-border text-nhp-text rounded-none"
                />
                <span className="text-nhp-muted">to</span>
                <input
                  type="date" data-testid="reports-revenue-to" value={to} min={from} max={isoDaysAgo(0)}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-9 px-2 bg-nhp-bg border border-nhp-border text-nhp-text rounded-none"
                />
              </div>
              {revenue && (
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Total</div>
                  <div className="font-mono font-bold text-2xl text-nhp-primary tabular-nums">
                    {KES(revenue.series.reduce((a, b) => a + b.revenue, 0))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {revenue && (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={revenue.series}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chart.accent} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={chart.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatTick} tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <YAxis tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <Tooltip
                  contentStyle={chart.tooltip}
                  labelStyle={chart.tooltipLabel} itemStyle={chart.tooltipItem}
                  labelFormatter={formatTick}
                  formatter={(v) => KES(v)}
                />
                <Area type="monotone" dataKey="revenue" stroke={chart.accent} strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// SITE PERFORMANCE</div>
          <div className="font-heading font-bold text-xl mt-1 mb-4">Revenue by Site</div>
          {sites && sites.sites.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={sites.sites}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} vertical={false} />
                <XAxis dataKey="name" tick={chart.tickLg} axisLine={chart.axisLine} tickLine={false} />
                <YAxis tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <Tooltip
                  contentStyle={chart.tooltip}
                  cursor={chart.tooltipCursor}
                  formatter={(v) => KES(v)}
                />
                <Bar dataKey="revenue" fill={chart.accent} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div data-testid="site-perf-empty" className="h-[320px] flex flex-col items-center justify-center text-center py-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// NO SITE-ATTRIBUTED REVENUE YET</div>
              <div className="mt-3 text-sm text-nhp-muted max-w-md">
                Once customers pay through a hotspot tied to a specific site, per-site revenue will appear here.
              </div>
            </div>
          )}
        </div>

        {sites && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sites.sites.map((s) => (
              <div key={s.name} className="border border-nhp-border bg-nhp-surface p-5">
                <div className="font-heading font-bold text-lg">{s.name}</div>
                <div className="text-xs text-nhp-muted">{s.location}</div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-nhp-border">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Revenue</div>
                    <div className="font-mono font-bold text-nhp-primary tabular-nums text-sm mt-1">{KES(s.revenue)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Txns</div>
                    <div className="font-mono font-bold tabular-nums text-sm mt-1">{s.transactions}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Live</div>
                    <div className="font-mono font-bold tabular-nums text-sm mt-1 text-nhp-info">{s.active_sessions}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
